import { useMemo } from 'react'

import { Box, Typography } from '@mui/material'
import { Alert, AlertSeverityEnum } from '@nativewaves/nw-cloud-sdk/content'
import { ColumnDef } from '@tanstack/react-table'
import { format, fromUnixTime } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { ThemeTypes } from 'stores/settings'
import { getAlertSeverityColor } from 'utils'

export const useColumns = () => {
  const { t } = useTranslation(['common', 'domain'])

  return useMemo<ColumnDef<Alert>[]>(
    () => [
      {
        accessorKey: 'severity',
        header: () => null,
        meta: {
          sizing: {
            width: 48,
            px: 2,
          },
        },
        cell: ({ getValue }) => (
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: '50%',
              backgroundColor: (theme) =>
                theme.palette.mode === ThemeTypes.LIGHT
                  ? getAlertSeverityColor(getValue<AlertSeverityEnum>())[500]
                  : getAlertSeverityColor(getValue<AlertSeverityEnum>())[700],
            }}
          />
        ),
      },
      {
        accessorKey: 'code',
        header: t('domain:ContentProcessing.Instance.code'),
        meta: {
          sizing: {
            minWidth: 160,
          },
        },
        cell: (cell) => (
          <Typography variant="inherit" noWrap>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'name',
        header: t('name'),
        meta: {
          sizing: {
            minWidth: 240,
          },
        },
        cell: (cell) => (
          <Typography variant="inherit" noWrap>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'details',
        header: t('domain:ContentProcessing.Instance.detail', { count: 2 }),
        meta: {
          sizing: {
            minWidth: 300,
            px: 3,
          },
        },
        cell: (cell) => (
          <Typography variant="inherit" noWrap>
            {cell.getValue<string>()}
          </Typography>
        ),
      },

      {
        accessorKey: 'timestamp',
        header: t('domain:ContentProcessing.Instance.dateTime'),
        meta: {
          sizing: {
            minWidth: 120,
          },
        },
        cell: (cell) =>
          cell.getValue<number>() > 0 && (
            <Typography variant="inherit">
              {format(fromUnixTime(cell.getValue<number>()), 'dd. MMM. yyyy - hh:mm a')}
            </Typography>
          ),
      },
    ],
    [t],
  )
}
